import React, { useState, useEffect } from 'react';
import { AppDrawer } from '../ui/AppDrawer';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { IntegerQuantityInput } from '../IntegerQuantityInput';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Truck, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { vehicleToVehicleAPI } from '../../lib/api';

export const VehicleToVehicleTransferModal = ({
  open,
  onClose,
  sourceVanId,
  sourceVanName,
  inventory,
  otherVans,
  preSelectedItemId = null
}) => {
  const [selectedItemId, setSelectedItemId] = useState('');
  const [destinationVanId, setDestinationVanId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const selectedItem = inventory.find(i => i.item_id === selectedItemId);
  const maxQty = selectedItem?.qty_on_hand || 0;
  const destinationVan = otherVans.find(v => v.id === destinationVanId);

  useEffect(() => {
    if (open && preSelectedItemId) setSelectedItemId(preSelectedItemId);
  }, [open, preSelectedItemId]);

  const handleClose = (success = false) => {
    setSelectedItemId('');
    setDestinationVanId('');
    setQuantity('1');
    setNote('');
    onClose(success);
  };

  const handleSubmit = async () => {
    if (!selectedItemId) { toast.error('Please select an item'); return; }
    if (!destinationVanId) { toast.error('Please select a destination vehicle'); return; }
    const qty = parseInt(quantity);
    if (!qty || qty <= 0) { toast.error('Quantity must be greater than 0'); return; }
    if (qty > maxQty) { toast.error(`Cannot transfer more than ${maxQty} items`); return; }

    setSubmitting(true);
    try {
      await vehicleToVehicleAPI.create({
        source_van_id: sourceVanId,
        destination_van_id: destinationVanId,
        item_id: selectedItemId,
        quantity: qty,
        note: note || null,
      });
      toast.success('Transfer request submitted! Awaiting approval.');
      handleClose(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit transfer request');
    } finally {
      setSubmitting(false);
    }
  };

  const isDirty = selectedItemId !== '' || note !== '' || quantity !== '1';

  return (
    <AppDrawer
      open={open}
      onOpenChange={(nextOpen) => { if (!nextOpen) handleClose(); }}
      title={
        <span className="flex items-center gap-2">
          <Truck className="w-5 h-5 text-primary" />
          Transfer to Another Vehicle
        </span>
      }
      description={`Request items to be transferred from ${sourceVanName} to another vehicle. This will require owner approval.`}
      size="md"
      isDirty={isDirty}
      data-testid="v2v-transfer-drawer"
      footer={
        <>
          <Button variant="outline" onClick={() => handleClose()} disabled={submitting} data-testid="v2v-transfer-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || !selectedItemId || !destinationVanId} data-testid="v2v-transfer-submit-btn">
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</>
            ) : (
              'Submit Request'
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Item Selection */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Select Item</Label>
          <Select value={selectedItemId} onValueChange={setSelectedItemId}>
            <SelectTrigger className="h-12" data-testid="v2v-item-select">
              <SelectValue placeholder="Choose an item..." />
            </SelectTrigger>
            <SelectContent>
              {inventory.filter(i => i.qty_on_hand > 0).map((item) => (
                <SelectItem key={item.item_id} value={item.item_id}>
                  {item.item_name} ({item.qty_on_hand} available)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Destination Vehicle Selection */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Destination Vehicle</Label>
          <Select value={destinationVanId} onValueChange={setDestinationVanId}>
            <SelectTrigger className="h-12" data-testid="v2v-destination-select">
              <SelectValue placeholder="Choose destination vehicle..." />
            </SelectTrigger>
            <SelectContent>
              {otherVans.map((van) => (
                <SelectItem key={van.id} value={van.id}>{van.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Visual Transfer Indicator */}
        {selectedItem && destinationVan && (
          <div className="flex items-center justify-center gap-3 py-3 px-4 rounded-lg bg-muted/50 text-sm" data-testid="v2v-transfer-indicator">
            <span className="font-medium truncate">{sourceVanName}</span>
            <ArrowRight className="w-4 h-4 text-primary shrink-0" />
            <span className="font-medium truncate">{destinationVan.name}</span>
          </div>
        )}

        {/* Quantity */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">
            Quantity {maxQty > 0 && <span className="text-muted-foreground font-normal">(max: {maxQty})</span>}
          </Label>
          <IntegerQuantityInput min={1} max={maxQty} value={quantity}
            onChange={(val) => setQuantity(val)} className="h-12 text-lg font-mono"
            data-testid="v2v-transfer-quantity-input" />
        </div>

        {/* Note */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Note (Optional)</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Why do you need this transfer?" rows={2} className="resize-none"
            data-testid="v2v-transfer-note-input" />
        </div>
      </div>
    </AppDrawer>
  );
};
